import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { TabletButton } from '@campfire/tablet-button';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { RoleIcon } from '../../../../common/icons/RoleIconList';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';

interface RoleOption {
  activityRoleId: string;
  name: string;
  icon: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  volunteerIds: string[];
  roles: RoleOption[];
  onSuccess: () => void;
}

export const BulkRoleEnrolDialog: React.FC<Props> = ({ open, onClose, volunteerIds, roles, onSuccess }) => {
  const { theme } = useCampfireTheme();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const { run } = useCampfireFetch({ defer: true });

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((prev) => (prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]));
  };

  const handleClose = () => {
    setSelectedRoleIds([]);
    onClose();
  };

  const handleSubmit = () => {
    setSubmitting(true);
    Promise.all(
      selectedRoleIds.map((roleId) =>
        run({
          url: `${apiUrl}/vm/activity-roles/enrol-bulk`,
          method: 'post',
          data: { activityRoleId: roleId, volunteerIds },
        })
      )
    ).then((results) => {
      const failed = results.find((res: any) => !res?.ok);
      if (failed) {
        setSnackbar({ variant: 'error', message: 'Failed to enrol in role(s)', open: true });
      } else {
        setSnackbar({ variant: 'success', message: 'Volunteers enrolled in role(s)', open: true });
        setSelectedRoleIds([]);
        onSuccess();
        onClose();
      }
      setSubmitting(false);
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Enrol in Roles</DialogTitle>
      <DialogContent style={{ padding: 0 }}>
        {roles.length === 0 ? (
          <Box padding={3}>
            <Typography color='textSecondary'>No roles available.</Typography>
          </Box>
        ) : (
          <List style={{ paddingBottom: 0 }}>
            {roles.map((role) => (
              <ListItem
                key={role.activityRoleId}
                button
                onClick={() => toggleRole(role.activityRoleId)}
                style={{ borderBottom: `1px solid ${theme.color.grey.border}`, padding: '12px 24px' }}
              >
                <ListItemIcon style={{ minWidth: 40 }}>
                  <Checkbox
                    checked={selectedRoleIds.includes(role.activityRoleId)}
                    edge='start'
                    disableRipple
                    color='primary'
                  />
                </ListItemIcon>
                {role.icon && (
                  <ListItemIcon style={{ minWidth: 32 }}>
                    <RoleIcon iconName={role.icon} color={theme.color.grey.neutral300} style={{ height: 18 }} />
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={
                    <Typography style={{ fontWeight: 600, fontSize: 15, color: '#444' }}>{role.name}</Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Box padding={1} display='flex' justifyContent='flex-end' width='100%'>
          <TabletButton variant='text' onClick={handleClose} disabled={submitting} style={{ marginRight: 8 }}>
            Cancel
          </TabletButton>
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={selectedRoleIds.length === 0 || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Enrol'}
          </TabletButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
